import { stringifyGraph } from '@/clients/http'
import { UserModel } from '@/http/models/User'
import { toast } from 'sonner'

import { useState } from 'react'

import { UserModel as UserType } from '@/types'

import { useFetchUsers } from './useFetchUsers'

type DialogMode = 'create' | 'edit' | 'view'

export const useUsersTable = () => {
  const [mode, setMode] = useState<DialogMode>('create')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editUser, setEditUser] = useState<UserType.User | null>(null)

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState<UserType.User | null>(null)

  // A staff member's POS offline PIN, shown once after create or reset: the server never returns it again.
  const [shownPin, setShownPin] = useState<{ name: string; pin: string } | null>(null)

  const [createLoading, setCreateLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)

  const { data, loading, pagination, setPage, setPageSize, refresh, filters, formMethods } =
    useFetchUsers()

  const openCreateDialog = () => {
    setMode('create')
    setEditUser(null)
    setDialogOpen(true)
  }

  const openViewDialog = (user: UserType.User) => {
    setMode('view')
    setEditUser(user)
    setDialogOpen(true)
  }

  const openEditDialog = async (user: UserType.User) => {
    setMode('edit')
    setUpdateLoading(true)

    const response = await UserModel.getById({
      params: { id: user._id },
      query: {
        graph_enc: stringifyGraph({
          '*': true,
          roles: {
            '*': true,
          },
          image: {
            path: true,
            versions: true,
          },
        }),
      },
    })

    if (response.success && response.data) {
      setEditUser(response.data.result)
      setDialogOpen(true)
    } else {
      toast.error('Failed to fetch user details')
    }
    setUpdateLoading(false)
  }

  const closeDialog = () => {
    setDialogOpen(false)
    setEditUser(null)
  }

  const handleCreateUser = async (data: any) => {
    setCreateLoading(true)
    const response = await UserModel.create({
      body: data,
    })

    if (response.success) {
      toast.success('User created successfully')
      closeDialog()
      refresh()
      const pin = (response.data as any)?.offline_pin
      if (pin) setShownPin({ name: data.name, pin })
    } else {
      toast.error(response.error?.data?.message || 'Failed to create user')
    }
    setCreateLoading(false)
  }

  const handleUpdateUser = async (id: string, data: any) => {
    setUpdateLoading(true)
    const response = await UserModel.update({
      params: { id },
      body: data,
    })

    if (response.success) {
      toast.success('User updated successfully')
      closeDialog()
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to update user')
    }
    setUpdateLoading(false)
  }

  const handleStatusToggle = async (user: UserType.User) => {
    const nextStatus = user.status === 'active' ? 'inactive' : 'active'
    const response = await UserModel.update({
      params: { id: user._id },
      body: { status: nextStatus },
    })

    if (response.success) {
      toast.success(`User ${nextStatus === 'active' ? 'activated' : 'deactivated'} successfully`)
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to update user status')
    }
  }

  const handleResetPin = async (user: UserType.User) => {
    const response = await UserModel.update({
      params: { id: user._id },
      body: { reset_offline_pin: true },
    })
    const pin = (response.data as any)?.offline_pin
    if (response.success && pin) {
      setShownPin({ name: user.name, pin })
    } else {
      toast.error(response.error?.data?.message || 'Failed to reset the PIN')
    }
  }

  return {
    loading,
    shownPin,
    setShownPin,
    handleResetPin,
    data: data as UserType.User[],
    pagination,
    mode,
    dialogOpen,
    editUser,
    deleteDialogOpen,
    userToDelete,
    createLoading,
    updateLoading,
    filters,
    formMethods,
    setPage,
    setPageSize,
    refresh,
    openCreateDialog,
    openViewDialog,
    openEditDialog,
    closeDialog,
    setDeleteDialogOpen,
    handleCreateUser,
    handleUpdateUser,
    handleStatusToggle,
  }
}
